import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import './TeacherRegister.css';

const TeacherRegister = () => {
  const [formData, setFormData] = useState({
    teacherName: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobileNumber: '',
    specialization: [], 
    levelsTaught: [], 
    educationalBoard: '',
    teachingExperience: '',
    state: '',
    areaOfResidence: '',
    postalPinCode: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Only allow digits in password and confirmPassword fields
    if (name === 'password' || name === 'confirmPassword') {
      // Remove any non-digit character
      const digitsOnly = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked 
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.teacherName.trim()) errors.teacherName = 'Teacher name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (!/^\d{6}$/.test(formData.password)) {
      errors.password = 'Password must be exactly 6 digits';
    }
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (!formData.mobileNumber.trim()) errors.mobileNumber = 'Mobile number is required';
    if (!/^\d{10}$/.test(formData.mobileNumber)) errors.mobileNumber = 'Mobile number must be 10 digits';
    if (formData.specialization.length === 0) errors.specialization = 'Please select at least one specialization';
    if (formData.levelsTaught.length === 0) errors.levelsTaught = 'Please select at least one level taught';
    if (!formData.educationalBoard) errors.educationalBoard = 'Please select an educational board';
    if (!formData.teachingExperience) errors.teachingExperience = 'Teaching experience is required';
    if (!formData.state.trim()) errors.state = 'State is required';
    if (!formData.areaOfResidence.trim()) errors.areaOfResidence = 'Area of residence is required';
    if (!formData.postalPinCode.trim()) errors.postalPinCode = 'Postal pin code is required';
    if (!/^\d{6}$/.test(formData.postalPinCode)) errors.postalPinCode = 'Postal pin code must be 6 digits';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post('/api/auth/teacher/register', formData);
      
      if (response.data.success) {
        // Show success state
        setRegistrationSuccess(true);
        toast.success('Registration successful! Redirecting to login...');
        
        // Redirect after 3 seconds to give user time to see the success message
        setTimeout(() => {
          navigate('/teacher/login');
        }, 3000);
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
      
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach(err => {
          backendErrors[err.path] = err.msg;
        });
        setValidationErrors(backendErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="teacher-register-container">
      {/* Floating Background Elements */}
      <div className="floating-shape shape-1"></div>
      <div className="floating-shape shape-2"></div>
      <div className="floating-shape shape-3"></div>
      <div className="floating-shape shape-4"></div>
      <div className="floating-shape shape-5"></div>
      <div className="floating-shape shape-6"></div>
      <div className="floating-shape bubble-1"></div>
      <div className="floating-shape bubble-2"></div>
      <div className="floating-shape bubble-3"></div>
      
      <div className="teacher-register-wrapper">
        <div className="teacher-register-card">
          <div className="register-header">
            <h1>Join as a Teacher Collaborator</h1>
            <p>We Elevate Learning Through Activities</p>
          </div>

          {/* Success Message */}
          {registrationSuccess && (
            <div className="registration-success-message">
              <div className="success-icon">✅</div>
              <div className="success-content">
                <h3>Registration Successful!</h3>
                <p>Your teacher account has been created successfully.</p>
                <p>You will be redirected to the login page in a few seconds...</p>
                <div className="success-actions">
                  <button 
                    type="button"
                    className="btn-login-now"
                    onClick={() => navigate('/teacher/login')}
                  >
                    Go to Login Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Only show form if registration is not successful */}
          {!registrationSuccess && (
            <form onSubmit={handleSubmit} className="register-form">
            {/* Basic Information Row */}
            <div className="form-row">
              {/* Teacher Name */}
              <div className="form-group">
                <label>
                  Teacher Name <span className="required-asterisk">*</span>
                  {validationErrors.teacherName && (
                    <span className="error-message-inline"> - {validationErrors.teacherName}</span>
                  )}
                </label>
                <input
                  type="text"
                  name="teacherName"
                  value={formData.teacherName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={validationErrors.teacherName ? 'error' : ''}
                  required
                />
              </div>

              {/* Email */}
              <div className="form-group">
                <label>
                  Email (Username) <span className="required-asterisk">*</span>
                  {validationErrors.email && (
                    <span className="error-message-inline"> - {validationErrors.email}</span>
                  )}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  className={validationErrors.email ? 'error' : ''}
                  required
                />
              </div>
            </div>

            {/* Contact Information Row */}
            <div className="form-row">
              {/* Mobile Number */}
              <div className="form-group">
                <label>
                  Mobile Number <span className="required-asterisk">*</span>
                  {validationErrors.mobileNumber && (
                    <span className="error-message-inline"> - {validationErrors.mobileNumber}</span>
                  )}
                </label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  className={validationErrors.mobileNumber ? 'error' : ''}
                  maxLength="10"
                  required
                />
              </div>

              {/* Teaching Experience */}
              <div className="form-group">
                <label>
                  Teaching Experience (years) <span className="required-asterisk">*</span>
                  {validationErrors.teachingExperience && (
                    <span className="error-message-inline"> - {validationErrors.teachingExperience}</span>
                  )}
                </label>
                <input
                  type="number"
                  name="teachingExperience"
                  value={formData.teachingExperience}
                  onChange={handleInputChange}
                  placeholder="Years of experience"
                  className={validationErrors.teachingExperience ? 'error' : ''}
                  min="0"
                  required
                />
              </div>
            </div>

            {/* Specialization */}
            <div className="form-group">
              <label>
                Specialization <span className="required-asterisk">*</span>
                {validationErrors.specialization && (
                  <span className="error-message-inline"> - {validationErrors.specialization}</span>
                )}
              </label>
              <div className="checkbox-group">
                {['English', 'Math', 'Science'].map(subject => (
                  <label key={subject} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="specialization"
                      value={subject}
                      checked={formData.specialization.includes(subject)}
                      onChange={handleInputChange}
                    />
                    <span>{subject}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Levels Taught */}
            <div className="form-group">
              <label>
                Levels Taught <span className="required-asterisk">*</span>
                {validationErrors.levelsTaught && (
                  <span className="error-message-inline"> - {validationErrors.levelsTaught}</span>
                )}
              </label>
              <div className="checkbox-group">
                {['Pre-Primary', 'Primary', 'High School', 'Higher Secondary'].map(level => (
                  <label key={level} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="levelsTaught"
                      value={level}
                      checked={formData.levelsTaught.includes(level)}
                      onChange={handleInputChange}
                    />
                    <span>{level}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Educational Board and State Row */}
            <div className="form-row">
              {/* Educational Board */}
              <div className="form-group">
                <label>
                  Educational Board <span className="required-asterisk">*</span>
                  {validationErrors.educationalBoard && (
                    <span className="error-message-inline"> - {validationErrors.educationalBoard}</span>
                  )}
                </label>
                <select
                  name="educationalBoard"
                  value={formData.educationalBoard}
                  onChange={handleInputChange}
                  className={validationErrors.educationalBoard ? 'error' : ''}
                  required
                >
                  <option value="">Select Educational Board</option>
                  <option value="CBSE">CBSE</option>
                  <option value="ICSE">ICSE</option>
                  <option value="State Board">State Board</option>
                  <option value="International">International</option>
                  <option value="Anganwadi/Pre-primary">Anganwadi/Pre-primary</option>
                </select>
              </div>

              {/* State */}
              <div className="form-group">
                <label>
                  State <span className="required-asterisk">*</span>
                  {validationErrors.state && (
                    <span className="error-message-inline"> - {validationErrors.state}</span>
                  )}
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter your state"
                  className={validationErrors.state ? 'error' : ''}
                  required
                />
              </div>
            </div>

            {/* Location Row */}
            <div className="form-row">
              {/* Area of Residence */}
              <div className="form-group">
                <label>
                  Area of Residence <span className="required-asterisk">*</span>
                  {validationErrors.areaOfResidence && (
                    <span className="error-message-inline"> - {validationErrors.areaOfResidence}</span>
                  )}
                </label>
                <input
                  type="text"
                  name="areaOfResidence"
                  value={formData.areaOfResidence}
                  onChange={handleInputChange}
                  placeholder="Enter your area/locality"
                  className={validationErrors.areaOfResidence ? 'error' : ''}
                  required
                />
              </div>

              {/* Postal Pin Code */}
              <div className="form-group">
                <label>
                  Postal Pin Code <span className="required-asterisk">*</span>
                  {validationErrors.postalPinCode && (
                    <span className="error-message-inline"> - {validationErrors.postalPinCode}</span>
                  )}
                </label>
                <input
                  type="text"
                  name="postalPinCode"
                  value={formData.postalPinCode}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit pin code"
                  className={validationErrors.postalPinCode ? 'error' : ''}
                  maxLength="6"
                  required
                />
              </div>
            </div>

            {/* Password Row */}
            <div className="form-row">
              {/* Password */}
              <div className="form-group">
                <label>
                  Create Password <span className="required-asterisk">*</span>
                  {validationErrors.password && (
                    <span className="error-message-inline"> - {validationErrors.password}</span>
                  )}
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onKeyPress={e => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter 6-digit password"
                    className={validationErrors.password ? 'error' : ''}
                    required
                    maxLength={6}
                    pattern="\d{6}"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {showPassword ? (
                        // Eye off icon (password visible, click to hide)
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </>
                      ) : (
                        // Eye on icon (password hidden, click to show)
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label>
                  Confirm Password <span className="required-asterisk">*</span>
                  {validationErrors.confirmPassword && (
                    <span className="error-message-inline"> - {validationErrors.confirmPassword}</span>
                  )}
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onKeyPress={e => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Re-enter your password"
                    className={validationErrors.confirmPassword ? 'error' : ''}
                    required
                    maxLength={6}
                    pattern="\d{6}"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {showConfirmPassword ? (
                        // Eye off icon (password visible, click to hide)
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </>
                      ) : (
                        // Eye on icon (password hidden, click to show)
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register'}
            </button>

            {/* Login Link */}
            <div className="login-link">
              Already have an account? <a href="/teacher/login">Login here</a>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherRegister;
